import { Button, Divider, Stack, Typography } from '@mui/material'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useTransactionState } from '@/atoms'
import { usePlanningRangeModal } from '@/components/modals/PlanningRangeModal'
import { ReadOnlyTooltip } from '@/components/smartUtils/plan/ReadOnlyTooltip'
import { selectUserConfiguration } from '@/features/domain/user'
import { useNavigate } from '@/routing'

import { StorageSection } from './components'
import { useBorderRestriction } from './hooks/useBorderRestriction'
import { usePlanningRange } from './hooks/usePlanningRange'
import { useTexts } from './hooks/useTexts'
import { useTimezone } from './hooks/useTimezone'

import { preview } from '@/components/form/preview'

export function Territory() {
  const texts = useTexts()
  const timezone = useTimezone()
  const navigate = useNavigate(true)
  const { planType } = useSelector(selectUserConfiguration)
  const { planningRange, canEditPlanningRange } = usePlanningRange()
  const transactionState = useTransactionState()
  const transactionInProgressFromCurrentUser = transactionState.mode === 'controlled'

  const borderRestriction = useBorderRestriction()
  const hasBorderRestriction = borderRestriction.code !== ''

  const isArchived = planType === 'archived'

  const { Modal: PlanningRangeModal, show: showPlanningRangeModal } = usePlanningRangeModal()

  const navigateToOrders = useCallback(() => {
    navigate('orders')
  }, [navigate])

  return (
    <>
      <preview.Root
        testId="territory-view"
        applyPendingFitMapOnMount={false}
        rightBorder={false}
        header={
          <preview.Header
            title={texts.title}
            subtitle={texts.description}
            testId="territory-view-header"
          >
            <></>
          </preview.Header>
        }
      >
        {!isArchived && (
          <>
            <StorageSection
              navigateToOrders={navigateToOrders}
              openEditPlanningRange={showPlanningRangeModal}
            />

            <Divider sx={{ mb: 4 }} />
          </>
        )}

        {planType !== 'simulation' && (
          <>
            <Stack direction="row" alignItems="flex-start" data-testid="planning-range">
              <preview.Field
                label={texts.planningRange}
                testId="planning-range-details"
                variant="alternative"
              >
                {planningRange} {texts.days}
              </preview.Field>
              {canEditPlanningRange && (
                <ReadOnlyTooltip
                  canEdit={!transactionInProgressFromCurrentUser}
                  title={texts.transactionInProgressTitle}
                  subtitle={texts.transactionInProgressSubtitle}
                  render={preventEditing => (
                    <Button
                      variant="outlined"
                      sx={{ ml: 2, mt: 1 }}
                      onClick={showPlanningRangeModal}
                      disabled={preventEditing}
                      data-testid="planning-range-edit"
                    >
                      {texts.edit}
                    </Button>
                  )}
                />
              )}
            </Stack>
            <Divider sx={{ mb: 4 }} />
          </>
        )}

        <preview.Field label={texts.timezone} testId="timezone">
          {timezone}
        </preview.Field>

        <Stack direction="column" spacing={1}>
          <preview.Field label={texts.borderRestrictionLabel} testId="border-restriction">
            {hasBorderRestriction ? borderRestriction.name : texts.noBorderRestriction}
          </preview.Field>

          {hasBorderRestriction && (
            <Typography
              variant="body2"
              color="textSecondary"
              fontSize={12}
              data-testid="border-restriction-note"
            >
              {texts.borderRestrictionNote}
            </Typography>
          )}
        </Stack>
      </preview.Root>

      <PlanningRangeModal />
    </>
  )
}
